import { useCallback, useEffect, useState } from "react";
import { format, addMonths, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCcw, MapPin, ExternalLink, AlertTriangle, Calendar as CalIcon } from "lucide-react";
import { formatDate } from "@/lib/dates";

const TYPE_TONE = {
  event: "bg-violet-100 text-violet-800 border-violet-300",
  activity: "bg-emerald-100 text-emerald-800 border-emerald-300",
  food: "bg-amber-100 text-amber-800 border-amber-300",
  beach: "bg-sky-100 text-sky-800 border-sky-300",
  default: "bg-slate-100 text-slate-700 border-slate-300",
};

export default function GuideCalendarTab() {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [data, setData] = useState({ ok: false, events: [], error: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const from_date = format(startOfMonth(month), "yyyy-MM-dd");
    const to_date = format(endOfMonth(month), "yyyy-MM-dd");
    try {
      const res = await api.get("/admin/guide-calendar", { params: { from_date, to_date } });
      setData(res.data);
    } catch (err) {
      setData({ ok: false, events: [], error: err.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const groupedByDate = (data.events || []).reduce((acc, e) => {
    const k = e.date;
    if (!acc[k]) acc[k] = [];
    acc[k].push(e);
    return acc;
  }, {});
  const sortedDates = Object.keys(groupedByDate).sort();

  return (
    <section data-testid="guide-calendar-tab">
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonth(subMonths(month, 1))} aria-label="Mese precedente">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-display text-2xl text-[#1E3A5F] capitalize min-w-[180px] text-center">
            {format(month, "MMMM yyyy", { locale: itLocale })}
          </div>
          <Button variant="outline" size="icon" onClick={() => setMonth(addMonths(month, 1))} aria-label="Mese successivo">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <Button
          onClick={() => { load(); toast.success("Calendario aggiornato"); }}
          disabled={loading}
          className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
          data-testid="guide-refresh"
        >
          <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      {!data.ok && data.error && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-4 flex items-start gap-3" data-testid="guide-error">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 space-y-2">
            <p className="font-semibold">Calendario non ancora collegato all'altra tua app.</p>
            <p className="text-amber-800/90">
              Ho provato a contattare la tua app Digital Guide, ma non risponde a nessuno dei
              percorsi che il sito si aspetta. Devi <strong>chiedere all'altra app di creare</strong>{" "}
              uno di questi endpoint pubblici (URL):
            </p>
            <ul className="list-disc pl-5 text-xs space-y-0.5 font-mono bg-amber-100/60 rounded p-2">
              <li>GET /api/calendar/public?from_date=YYYY-MM-DD&amp;to_date=YYYY-MM-DD</li>
              <li>GET /api/events/public</li>
              <li>GET /api/guide/events</li>
            </ul>
            <p className="text-amber-800/90">
              Deve restituire un JSON tipo questo:
            </p>
            <pre className="text-[11px] bg-amber-100/60 rounded p-2 overflow-x-auto whitespace-pre">
{`[
  {
    "id": "evt-001",
    "date": "2026-08-15",
    "title": "Festa di Ferragosto",
    "description": "Falò sulla spiaggia con musica",
    "type": "event",
    "image_url": "https://...",
    "location": "Lido Comunale",
    "link": "https://..."
  }
]`}
            </pre>
            <p className="text-amber-800/80 text-xs">
              Solo <code>id</code>, <code>date</code> e <code>title</code> sono obbligatori. Gli altri
              campi sono facoltativi.
            </p>
            <p className="text-[11px] text-amber-700/70 italic">
              Dettaglio tecnico: {String(data.error)}
            </p>
          </div>
        </div>
      )}

      {loading && (
        <p className="text-center text-[#635F59] py-6">Caricamento eventi…</p>
      )}

      {!loading && data.ok && sortedDates.length === 0 && (
        <div className="bg-white border border-[#E8E2D9] rounded-xl p-10 text-center" data-testid="guide-empty">
          <CalIcon className="w-10 h-10 text-[#635F59]/40 mx-auto mb-3" />
          <p className="text-[#635F59]">Nessun evento dalla guida per questo mese.</p>
        </div>
      )}

      {!loading && sortedDates.length > 0 && (
        <div className="space-y-3" data-testid="guide-events-list">
          {sortedDates.map((d) => (
            <div key={d} className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden">
              <div className="bg-[#FDFBF7] px-4 py-2 border-b border-[#E8E2D9]">
                <span className="font-semibold text-[#1E3A5F]">{formatDate(d)}</span>
                <span className="text-xs text-[#635F59] ml-2 capitalize">
                  {format(new Date(d), "EEEE", { locale: itLocale })}
                </span>
              </div>
              <ul className="divide-y divide-[#F2EEE7]">
                {groupedByDate[d].map((ev) => (
                  <li key={ev.id} className="flex gap-4 p-4" data-testid={`guide-event-${ev.id}`}>
                    {ev.image_url && (
                      <img
                        src={ev.image_url}
                        alt={ev.title}
                        className="w-20 h-20 rounded-lg object-cover shrink-0"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <h4 className="font-semibold text-[#1E3A5F] leading-snug">{ev.title}</h4>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${TYPE_TONE[ev.type] || TYPE_TONE.default}`}>
                          {ev.type || "evento"}
                        </span>
                      </div>
                      {ev.description && (
                        <p className="text-sm text-[#635F59] mt-1 line-clamp-2">{ev.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-[#635F59]">
                        {ev.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {ev.location}
                          </span>
                        )}
                        {ev.link && (
                          <a
                            href={ev.link}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1 text-[#AC5D45] hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" /> apri
                          </a>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
